import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useModuleSession } from "@/contexts/ModuleSessionContext";
import { isTenantModuleEnabled } from "@/lib/module-key-aliases";

/**
 * Moduli attivi del tenant (crm, billing, vertical_edilizia, …).
 *
 * Fonte primaria: tenantId già salvato in sessione modulo al login
 * (evita la race getUser → get_user_tenant che al primo paint nasconde Clienti/Fatturazione).
 * Fallback: waterfall auth + RPC come prima.
 */
export function useEnabledModules() {
  const { impersonatedTenant, isImpersonating } = useImpersonation();
  const { constructionSession, crmSession, currentModule } = useModuleSession();
  const queryClient = useQueryClient();

  // Impersonazione applicata solo su Core (preview moduli tenant)
  const applyImpersonation = isImpersonating && currentModule === "core";

  // Tenant già noto dalla sessione di login (construction o CRM)
  const sessionTenantId =
    constructionSession.tenantId || crmSession.tenantId || null;

  // Invalida cache moduli quando cambia la sessione auth (login/logout/switch)
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["current-user"] });
        queryClient.invalidateQueries({ queryKey: ["user-tenant"] });
        queryClient.invalidateQueries({ queryKey: ["tenant-modules"] });
      }, 0);
    });
    return () => subscription.unsubscribe();
  }, [queryClient]);

  const { data: currentUser, isLoading: isLoadingUser } = useQuery({
    queryKey: ["current-user"],
    queryFn: async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      return user;
    },
    // Se abbiamo già il tenant in sessione, non serve (e non rischiamo null cached)
    enabled: !sessionTenantId && !applyImpersonation,
    staleTime: 30 * 1000,
  });

  const { data: userTenantId, isLoading: isLoadingUserTenant } = useQuery({
    queryKey: ["user-tenant", currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return null;
      const { data } = await supabase.rpc("get_user_tenant", {
        _user_id: currentUser.id,
      });
      return data;
    },
    enabled: !!currentUser?.id && !applyImpersonation && !sessionTenantId,
    staleTime: 30 * 1000,
  });

  // Priorità: impersonazione (solo Core) → sessione modulo → RPC
  const activeTenantId = applyImpersonation
    ? impersonatedTenant?.id
    : sessionTenantId || userTenantId || null;

  const {
    data: modules,
    isLoading: isLoadingModules,
    isFetching: isFetchingModules,
  } = useQuery({
    queryKey: ["tenant-modules", activeTenantId],
    queryFn: async () => {
      if (!activeTenantId) return [];

      const { data, error } = await supabase
        .from("tenant_modules")
        .select("*")
        .eq("tenant_id", activeTenantId);

      if (error) throw error;
      return data || [];
    },
    enabled: !!activeTenantId,
    staleTime: 30 * 1000,
  });

  // In caricamento finché non abbiamo tenant o finché i moduli non sono arrivati
  const isResolvingTenant =
    !applyImpersonation &&
    !sessionTenantId &&
    (isLoadingUser || (!!currentUser?.id && isLoadingUserTenant));

  const isLoading =
    isResolvingTenant ||
    (!!activeTenantId && (isLoadingModules || (isFetchingModules && !modules)));

  const isModuleEnabled = (moduleKey: string) => {
    // Finché non sappiamo, non nascondere in modo "definitivo" a valle:
    // i caller che aspettano isLoading evitano il flash; qui restiamo conservativi
    if (isLoading || !modules) return false;
    return isTenantModuleEnabled(modules, moduleKey);
  };

  return { isModuleEnabled, isLoading, activeTenantId };
}
